// Developer: Ankit Gupta
import { Tag } from 'antd';
import type { RunStatus, ProjectStatus, IssueStatus, IssueSeverity } from '@/types';
import { useT } from '@/i18n';

// The status is the server's value; the label is looked up in the reader's
// language, with the English label as the fallback for a value not listed.

// ─── Run status ───────────────────────────────────────────────────────────────

const RUN_STATUS_CONFIG: Record<
  RunStatus,
  { color: string; label: string }
> = {
  pending:    { color: 'default',  label: 'Pending' },
  running:    { color: 'processing', label: 'Running' },
  validating: { color: 'blue',     label: 'Validating' },
  importing:  { color: 'geekblue', label: 'Importing' },
  completed:  { color: 'success',  label: 'Completed' },
  failed:     { color: 'error',    label: 'Failed' },
  cancelled:  { color: 'warning',  label: 'Cancelled' },
};

interface RunStatusBadgeProps {
  status: RunStatus;
}

export function RunStatusBadge({ status }: RunStatusBadgeProps) {
  const { t } = useT('common');
  const config = RUN_STATUS_CONFIG[status] ?? {
    color: 'default',
    label: status,
  };
  return <Tag color={config.color}>{t(`status.run.${status}`, { defaultValue: config.label })}</Tag>;
}

// ─── Project status ───────────────────────────────────────────────────────────

const PROJECT_STATUS_CONFIG: Record<
  string,
  { color: string; label: string }
> = {
  REQUESTED: { color: 'processing', label: 'Requested' },
  APPROVED:  { color: 'success',    label: 'Approved' },
  REJECTED:  { color: 'error',      label: 'Rejected' },
  CLOSED:    { color: 'default',    label: 'Closed' },
};

interface ProjectStatusBadgeProps {
  status: ProjectStatus | string;
}

export function ProjectStatusBadge({ status }: ProjectStatusBadgeProps) {
  const { t } = useT('common');
  const config = PROJECT_STATUS_CONFIG[status] ?? {
    color: 'default',
    label: status,
  };
  return <Tag color={config.color}>{t(`status.project.${status}`, { defaultValue: config.label })}</Tag>;
}

// ─── Issue status ─────────────────────────────────────────────────────────────

const ISSUE_STATUS_CONFIG: Record<
  IssueStatus,
  { color: string; label: string }
> = {
  open:        { color: 'error',   label: 'Open' },
  in_progress: { color: 'processing', label: 'In Progress' },
  resolved:    { color: 'success', label: 'Resolved' },
  closed:      { color: 'default', label: 'Closed' },
};

interface IssueStatusBadgeProps {
  status: IssueStatus;
}

export function IssueStatusBadge({ status }: IssueStatusBadgeProps) {
  const { t } = useT('common');
  const config = ISSUE_STATUS_CONFIG[status] ?? {
    color: 'default',
    label: status,
  };
  return <Tag color={config.color}>{t(`status.issue.${status}`, { defaultValue: config.label })}</Tag>;
}

// ─── Issue severity ───────────────────────────────────────────────────────────

const SEVERITY_CONFIG: Record<
  IssueSeverity,
  { color: string; label: string }
> = {
  critical: { color: 'red',    label: 'Critical' },
  high:     { color: 'orange', label: 'High' },
  medium:   { color: 'gold',   label: 'Medium' },
  low:      { color: 'blue',   label: 'Low' },
};

interface SeverityBadgeProps {
  severity: IssueSeverity;
}

export function SeverityBadge({ severity }: SeverityBadgeProps) {
  const { t } = useT('common');
  const config = SEVERITY_CONFIG[severity] ?? {
    color: 'default',
    label: severity,
  };
  return <Tag color={config.color}>{t(`status.severity.${severity}`, { defaultValue: config.label })}</Tag>;
}

// ─── Generic StatusBadge (accepts any string, maps known values) ──────────────

type AnyStatus = RunStatus | ProjectStatus | IssueStatus;

interface StatusBadgeProps {
  status: AnyStatus | string;
  type?: 'run' | 'project' | 'issue';
}

/**
 * Generic badge that dispatches to the right typed badge.
 * Falls back to a plain tag when the type is unknown.
 */
export function StatusBadge({ status, type }: StatusBadgeProps) {
  if (type === 'run')
    return <RunStatusBadge status={status as RunStatus} />;
  if (type === 'project')
    return <ProjectStatusBadge status={status as ProjectStatus} />;
  if (type === 'issue')
    return <IssueStatusBadge status={status as IssueStatus} />;
  return <Tag>{status}</Tag>;
}

export default StatusBadge;
